import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 여러 오디오 파일을 순차적으로 재생하는 커스텀 훅
 * @param {Array} initialAudioFiles - 초기 오디오 파일 경로 배열
 * @returns {Object} - 오디오 큐 컨트롤러
 */
export const useAudioQueue = (initialAudioFiles = []) => {
    const [audioFiles, setAudioFiles] = useState(initialAudioFiles);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadError, setLoadError] = useState(false);
    const audioRef = useRef(new Audio());
    const queueRef = useRef([...initialAudioFiles]);

    // 오디오 큐 설정
    useEffect(() => {
        queueRef.current = [...audioFiles];
        setCurrentIndex(0);
    }, [audioFiles]);

    // 오디오 파일 로드 및 재생 함수
    const playCurrentAudio = useCallback(() => {
        if (queueRef.current.length === 0 || currentIndex >= queueRef.current.length) {
            setIsPlaying(false);
            return;
        }

        const audio = audioRef.current;
        const currentFile = queueRef.current[currentIndex];

        console.log(`오디오 ${currentIndex + 1}/${queueRef.current.length} 재생 준비: ${currentFile}`);

        // 이전 이벤트 리스너 제거
        audio.oncanplay = null;
        audio.onerror = null;

        // 오디오 로드 완료 시 재생
        audio.oncanplay = () => {
            console.log(`오디오 ${currentIndex + 1}/${queueRef.current.length} 로드 완료, 재생 시작`);
            setLoadError(false);
            const playPromise = audio.play();

            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.error('오디오 재생 오류:', error);
                    handlePlayError();
                });
            }
        };

        // 오디오 로드 오류 처리
        audio.onerror = (error) => {
            console.error(`오디오 로드 오류 (${currentFile}):`, error);
            setLoadError(true);
            handlePlayError();
        };

        // 오디오 소스 설정 및 로드
        audio.src = currentFile;
        audio.volume = 1.0;
        audio.load();
    }, [currentIndex]);

    // 오류 발생 시 다음 파일로 넘어가는 함수
    const handlePlayError = useCallback(() => {
        if (currentIndex < queueRef.current.length - 1) {
            setCurrentIndex(prevIndex => prevIndex + 1);
        } else {
            setIsPlaying(false);
        }
    }, [currentIndex]);

    // 현재 오디오 파일이 끝났을 때 다음 파일 재생
    useEffect(() => {
        const audio = audioRef.current;

        const handleEnded = () => {
            console.log(`오디오 ${currentIndex + 1}/${queueRef.current.length} 재생 완료`);

            // 큐에 다음 파일이 있으면 재생
            if (currentIndex < queueRef.current.length - 1) {
                setCurrentIndex(prevIndex => prevIndex + 1);
            } else {
                // 모든 파일 재생 완료
                console.log('모든 오디오 재생 완료');
                setIsPlaying(false);
            }
        };

        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('ended', handleEnded);
        };
    }, [currentIndex]);

    // isPlaying 또는 currentIndex가 변경되면 오디오 재생 상태 업데이트
    useEffect(() => {
        if (isPlaying) {
            playCurrentAudio();
        } else {
            const audio = audioRef.current;
            audio.pause();
        }
    }, [isPlaying, currentIndex, playCurrentAudio]);

    // 오디오 파일 큐 설정 및 재생 시작
    const playAudioQueue = useCallback((files = []) => {
        // 이미 재생 중이면 중지
        if (isPlaying) {
            stopAudio();
        }

        // 파일 목록 확인 및 설정
        const filesToPlay = files.length > 0 ? files : queueRef.current;

        if (filesToPlay.length === 0) {
            console.warn('재생할 오디오 파일이 없습니다');
            return;
        }

        console.log('오디오 큐 설정:', filesToPlay);

        if (files.length > 0) {
            queueRef.current = [...files];
            setAudioFiles(files);
        }

        // 재생 상태 및 인덱스 초기화
        setCurrentIndex(0);
        setIsPlaying(true);
    }, [isPlaying]);

    // 오디오 파일 재생 완전히 중지 및 초기화하는 함수
    const stopAudio = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            // 모든 이벤트 리스너 제거
            audio.oncanplay = null;
            audio.onended = null;
            audio.onerror = null;

            // 재생 중지
            audio.pause();
            audio.currentTime = 0;

            // 중요: 소스 초기화로 진행 중인 로드 작업도 중단
            audio.src = '';

            try {
                // 로드 작업 강제 중단 시도
                audio.load();
            } catch (e) {
                // 오류 무시
            }
        }

        // 상태 초기화
        setIsPlaying(false);
        console.log('오디오 재생 중지 및 초기화 완료');
    }, []);

    // 현재 재생 중인 오디오 파일명 추출 (경로에서 파일명만)
    const getCurrentAudioName = useCallback(() => {
        if (!isPlaying || queueRef.current.length === 0 || currentIndex >= queueRef.current.length) {
            return null;
        }

        const fullPath = queueRef.current[currentIndex];
        // 파일명만 추출 (경로에서 마지막 '/' 이후의 문자열)
        return fullPath.substring(fullPath.lastIndexOf('/') + 1);
    }, [isPlaying, currentIndex]);

    return {
        isPlaying,
        currentIndex,
        totalCount: queueRef.current.length,
        currentAudio: getCurrentAudioName(),
        playAudioQueue,
        stopAudio,
        loadError,
        audioRef
    };
};

export default useAudioQueue;